import { hitTestN } from '../support/picking';
import { Observable, Scheduler } from '@reactivex/rxjs';
import { $ref, $atom } from '@graphistry/falcor-json-graph';
import { isAnimating, hitmapUpdates } from '@graphistry/viz/src/client/legacy';
import { tapDelay, SceneGestures, hitTestNTextures } from '../support';
import { SCENE_MOUSE_MOVE, SCENE_TOUCH_START } from '@graphistry/viz/src/actions/scene';

export function pickNodeSelection(actions) {

    const selectionStarts = SceneGestures.startFromActions(
        actions
            .ofType(SCENE_TOUCH_START)
            .filter(({ simulating, selectionMask, selectionType }) =>
                !simulating && !selectionMask && !selectionType
            )
    );

    const highlightMoves = SceneGestures.moveFromActions(
        actions
            .ofType(SCENE_MOUSE_MOVE)
            .filter(
                ({ event, simulating, selectionType }) =>
                    !simulating && !selectionType && event.buttons === 0
            )
    );

    // Use defer+repeat pattern for taps to avoid state accumulation
    const selectionTaps = Observable.defer(() => {
        const starts = SceneGestures.startFromActions(selectionStarts.take(1));
        return SceneGestures.tap(starts, { delay: tapDelay }).mergeAll();
    }).repeat();

    const movesWithHitmapUpdate = highlightMoves.combineLatest(hitmapUpdates, point => point);

    const movesWhileNotAnimating = isAnimating.switchMap(
        (animating = true) => (animating && Observable.empty()) || movesWithHitmapUpdate
    );

    const tapsWhileNotAnimating = isAnimating
        .switchMap((animating = true) => (animating && Observable.empty()) || selectionTaps)
        .mergeMap(tapWhenHitmapReady)
        .map(pointWithHitElement)
        .map(point => (point.type = 'tap') && point)
        .startWith({ time: 0 });

    const pointsAndElements = movesWhileNotAnimating
        .auditTime(100)
        .map(pointWithHitElement)
        .startWith({ time: 0 })
        .combineLatest(tapsWhileNotAnimating, (move, tap) => (move.time > tap.time) ? move : tap)
        .skip(1);

    const distinctPointsAndElements = pointsAndElements
        .distinctUntilChanged(filterDistinctPointsAndElements)
        .map(point => {
            const { element, element: { dim } } = point;
            element.type = dim === 1 ? 'point' : dim === 2 ? 'edge' : 'none';
            return point;
        });

    return distinctPointsAndElements.map(toValuesAndInvalidations);
}

//Moves retry stale hovers via combineLatest(hitmapUpdates); taps are one-shot,
//so a tap that lands while the lazy-picking hitmap readback is still in flight
//(a settle drops pixelreads.hitmap; it reappears once the readback completes)
//would hit-test against nothing and dispatch a deselection. Defer its hit test
//until the hitmap is back instead. renderPickingLazily covers a mouse tap with
//no move since the settle (mousedown deliberately doesn't trigger it -- see
//scene.js). Refreshing time keeps the deferred tap current in the move/tap
//arbitration below.
function tapWhenHitmapReady(point) {
    const hitmapReady = () => point.renderState.pixelreads.hitmap;
    if (hitmapReady()) {
        return Observable.of(point);
    }
    point.renderingScheduler.renderPickingLazily();
    return hitmapUpdates.filter(hitmapReady).take(1).map(() => {
        point.time = Date.now();
        return point;
    });
}

function pointWithHitElement(point) {
    point.element = hitTestN(point.renderState, hitTestNTextures, point.x, point.y, 10);
    return point;
}

function filterDistinctPointsAndElements(pointA, pointB) {
    if (!pointA || !pointB) {
        return true;
    } else if (pointA.type !== pointB.type || (pointB.type === 'tap')) {
        return false;
    }
    const { element: elementA } = pointA;
    const { element: elementB } = pointB;
    return elementA && elementB && elementA.dim === elementB.dim && elementA.idx === elementB.idx;
}

function toValuesAndInvalidations({ type, falcor, element, highlightEnabled, ...rest }) {
    return {
        falcor,
        ...(element.type !== 'none'
            ? selectionValuesAndInvalidations(type, element, falcor.getPath(), highlightEnabled)
            : deselectionValuesAndInvalidations(type, element))
    };
}

function selectionValuesAndInvalidations(gesture, { idx, type }, path, highlightEnabled) {
    const inverseType = type === 'point' ? 'edge' : 'point';
    return {
      values: [
        {
          json: {
            highlight: {
              darken: false,
              [type]: $atom([idx]),
              [inverseType]: $atom([]),
            },
            ...(gesture === "tap"
              ? {
                  labels: {
                    selection: $ref(path.concat("labelsByType", type, idx)),
                  },
                  selection: {
                    mask: null,
                    [type]: $atom([idx]),
                    [inverseType]: $atom([]),
                  },
                }
              : {
                  labels: {
                    highlight: $ref(path.concat("labelsByType", type, idx)),
                  },
                }),
          },
        },
      ],
      invalidations: [`selection['labels']`]
    };
}

function deselectionValuesAndInvalidations(gesture, { idx, type }) {
    const invalidations = [`highlight['edge', 'point']`, `selection['labels']`];
    const value = {
        highlight: {
            darken: false,
            edge: $atom([]),
            point: $atom([])
        }
    };
    if (gesture === 'tap') {
        invalidations.push(`selection['edge', 'point']`);
        value.labels = { selection: $atom(undefined) };
        value.selection = { edge: $atom([]), point: $atom([]) };
    } else {
        value.labels = { highlight: $atom(undefined) };
    }
    return { invalidations, values: [{ json: value }] };
}
